/**
 * 🥗 NutrientChart — reads the local food log and renders the user's
 * macro / micro breakdown for a chosen time window.
 *
 * Owner directive Feb 2026: "Nutrient chart is a NEED".
 *
 * Data source: `localStorage["omnimuscle_food_log_v1"]` (same key
 * FoodLog.jsx writes to). Each entry has:
 *   { id, date "YYYY-MM-DD", meal, name, servings, cal, p, c, f, fib, sug, na }
 *
 * We render (no chart library — plain CSS/SVG so it stays fast):
 *   1. Macro pie chart — % of calories from protein / carbs / fat
 *   2. Daily calorie bar chart across the window
 *   3. Detail rows — fiber, sugar, sodium (with soft "recommended" bar)
 *
 * Placement:
 *   Drop <NutrientChart /> anywhere. It self-loads and shows a modest
 *   "Log some meals to see your nutrition breakdown" state when empty.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Flame, Activity, ChevronDown, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOG_KEY = "omnimuscle_food_log_v1";

// ── PT date helper (matches FoodLog.todayKey) ──
function todayPTKey() {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Los_Angeles",
    year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(new Date());
  const y = parts.find((p) => p.type === "year")?.value;
  const m = parts.find((p) => p.type === "month")?.value;
  const d = parts.find((p) => p.type === "day")?.value;
  return `${y}-${m}-${d}`;
}

function daysBackKey(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Los_Angeles",
    year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(d);
  return `${parts.find((p) => p.type === "year").value}-${parts.find((p) => p.type === "month").value}-${parts.find((p) => p.type === "day").value}`;
}

// Simple SVG donut slice — no chart lib required
function DonutSlice({ startAngle, endAngle, radius, innerRadius, color, cx, cy }) {
  const startRad = (startAngle - 90) * (Math.PI / 180);
  const endRad = (endAngle - 90) * (Math.PI / 180);
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;
  const x1 = cx + radius * Math.cos(startRad);
  const y1 = cy + radius * Math.sin(startRad);
  const x2 = cx + radius * Math.cos(endRad);
  const y2 = cy + radius * Math.sin(endRad);
  const x3 = cx + innerRadius * Math.cos(endRad);
  const y3 = cy + innerRadius * Math.sin(endRad);
  const x4 = cx + innerRadius * Math.cos(startRad);
  const y4 = cy + innerRadius * Math.sin(startRad);
  return (
    <path
      d={`M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} L ${x3} ${y3} A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${x4} ${y4} Z`}
      fill={color}
    />
  );
}

// Nice number formatter — round + strip trailing .0
function n(v) {
  if (v == null || isNaN(v)) return "—";
  return Math.round(v).toLocaleString();
}

export function NutrientChart({ compact = false }) {
  const [log, setLog] = useState([]);
  const [quickEntries, setQuickEntries] = useState([]); // Quick Log extras from backend (today only)
  const [range, setRange] = useState("today"); // "today" | "week" | "month"
  // 🧪✨ Retro Macro Bulk Backfill state
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkReceipt, setBulkReceipt] = useState(null); // { scanned, updated, samples: [...] }

  const refreshQuick = async () => {
    try {
      const r = await axios.get(`${API}/vitals/today`, { withCredentials: true });
      setQuickEntries(r.data?.quick || []);
    } catch { setQuickEntries([]); }
  };

  // 🧪✨ One-tap bulk backfill — scans EVERY calorie-only Quick Log entry
  // across all dates, AI-estimates macros for each in parallel, and commits
  // the results server-side. Refreshes the local list so the donut updates.
  const runBulkBackfill = async () => {
    if (bulkBusy) return;
    setBulkBusy(true);
    try {
      const r = await axios.post(`${API}/vitals/quick/estimate-macros-bulk`);
      const data = r.data || {};
      setBulkReceipt(data);
      if ((data.updated || 0) > 0) {
        toast.success(
          `✨ Backfilled macros on ${data.updated} entr${data.updated === 1 ? "y" : "ies"}` +
          (data.skipped ? ` · ${data.skipped} skipped` : ""),
          { duration: 6000 },
        );
        await refreshQuick();
      } else if ((data.scanned || 0) === 0) {
        toast.info("Nothing to backfill — every entry already has macros. 🎉");
      } else {
        toast.error("AI couldn't estimate any of the entries. Try again in a moment.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk estimate failed.");
    } finally {
      setBulkBusy(false);
    }
  };

  useEffect(() => {
    try {
      const l = localStorage.getItem(LOG_KEY);
      if (l) setLog(JSON.parse(l));
    } catch { setLog([]); }
    refreshQuick();
    // Refresh when the tab regains focus (user might have logged in another tab)
    const onFocus = () => {
      try {
        const l = localStorage.getItem(LOG_KEY);
        setLog(l ? JSON.parse(l) : []);
      } catch { /* ignore */ }
      refreshQuick();
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  const scoped = useMemo(() => {
    const days = range === "today" ? 1 : range === "week" ? 7 : 30;
    const cutoff = daysBackKey(days - 1);
    return (log || []).filter((e) => e.date && e.date >= cutoff);
  }, [log, range]);

  // Quick-log entries are stored per-day on the backend under today only,
  // so they only contribute when the range includes today. The backend
  // `/vitals/today` gives us today's list; each entry has {calories} and
  // (post Feb 2026 fix) optional {protein_g, carbs_g, fat_g, fiber_g,
  // sugar_g, sodium_mg} macros for anything logged via Snap & Identify.
  const quickTotalsToday = useMemo(() => {
    return (quickEntries || []).reduce(
      (acc, q) => {
        acc.cal += Number(q.calories)  || 0;
        acc.p   += Number(q.protein_g) || 0;
        acc.c   += Number(q.carbs_g)   || 0;
        acc.f   += Number(q.fat_g)     || 0;
        acc.fib += Number(q.fiber_g)   || 0;
        acc.sug += Number(q.sugar_g)   || 0;
        acc.na  += Number(q.sodium_mg) || 0;
        return acc;
      },
      { cal: 0, p: 0, c: 0, f: 0, fib: 0, sug: 0, na: 0 }
    );
  }, [quickEntries]);
  const quickCalToday = quickTotalsToday.cal;
  // Count of quick entries that carry macro data (Snap-logged meals) —
  // used for the "Includes N Quick Log extras" caption to differentiate
  // full-macro entries from calorie-only ones.
  const quickWithMacrosCount = useMemo(() => {
    return (quickEntries || []).filter(
      (q) => (Number(q.protein_g) || 0) + (Number(q.carbs_g) || 0) + (Number(q.fat_g) || 0) > 0
    ).length;
  }, [quickEntries]);

  // Totals across the window — Food Log entries (with macros) + Quick Log
  // extras. Quick Log macros are folded in when today is in the window so
  // Snap-logged meals contribute their full P/C/F breakdown, not just kcal.
  const totals = useMemo(() => {
    const t = scoped.reduce(
      (acc, e) => {
        acc.cal += Number(e.cal) || 0;
        acc.p += Number(e.p) || 0;
        acc.c += Number(e.c) || 0;
        acc.f += Number(e.f) || 0;
        acc.fib += Number(e.fib) || 0;
        acc.sug += Number(e.sug) || 0;
        acc.na += Number(e.na) || 0;
        return acc;
      },
      { cal: 0, p: 0, c: 0, f: 0, fib: 0, sug: 0, na: 0 }
    );
    // Fold today's quick-log entries in — including macros — when today
    // is in the window. `range === "today"` is trivially in-window; for
    // "week"/"month" the today bucket also always exists in scoped so
    // we still add today's quick block once.
    t.cal += quickTotalsToday.cal;
    t.p   += quickTotalsToday.p;
    t.c   += quickTotalsToday.c;
    t.f   += quickTotalsToday.f;
    t.fib += quickTotalsToday.fib;
    t.sug += quickTotalsToday.sug;
    t.na  += quickTotalsToday.na;
    return t;
  }, [scoped, quickTotalsToday]);

  const days = range === "today" ? 1 : range === "week" ? 7 : 30;
  const avgCal = totals.cal / Math.max(1, days);

  // Macro % of calories — using standard atwater factors (4/4/9)
  const pCal = totals.p * 4;
  const cCal = totals.c * 4;
  const fCal = totals.f * 9;
  const calFromMacros = pCal + cCal + fCal;
  const macroSlices = calFromMacros > 0
    ? [
        { key: "protein", label: "Protein", grams: totals.p, cal: pCal, pct: pCal / calFromMacros, color: "#f472b6" },
        { key: "carbs",   label: "Carbs",   grams: totals.c, cal: cCal, pct: cCal / calFromMacros, color: "#60a5fa" },
        { key: "fat",     label: "Fat",     grams: totals.f, cal: fCal, pct: fCal / calFromMacros, color: "#fbbf24" },
      ]
    : [];

  // Per-day calorie bars for the window (includes today's Quick Log)
  const dailyBuckets = useMemo(() => {
    const buckets = {};
    for (let i = 0; i < days; i++) {
      const k = daysBackKey(days - 1 - i);
      buckets[k] = { date: k, cal: 0 };
    }
    for (const e of scoped) {
      if (buckets[e.date]) buckets[e.date].cal += Number(e.cal) || 0;
    }
    // Fold today's quick-log extras into today's bar
    const tk = todayPTKey();
    if (buckets[tk]) buckets[tk].cal += quickCalToday;
    return Object.values(buckets);
  }, [scoped, days, quickCalToday]);
  const maxDaily = Math.max(1, ...dailyBuckets.map((b) => b.cal));

  // Micro rows — informational only, soft "typical daily" reference
  const microRefs = [
    { key: "fib", label: "Fiber",  unit: "g",  daily: 28,   color: "#34d399" },
    { key: "sug", label: "Sugar",  unit: "g",  daily: 50,   color: "#f97316", warnHigh: true },
    { key: "na",  label: "Sodium", unit: "mg", daily: 2300, color: "#fb7185", warnHigh: true },
  ];

  return (
    <div
      className="rounded-xl border border-white/10 bg-black/50 backdrop-blur p-4 space-y-4"
      data-testid="nutrient-chart"
    >
      {/* Header + range picker */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-emerald-500/20 border border-emerald-400/40">
            <Activity size={14} className="text-emerald-300" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold">Nutrition</div>
            <div className="text-sm text-white/85 font-bold">Macros &amp; micros breakdown</div>
          </div>
        </div>
        <div className="relative">
          <select
            value={range}
            onChange={(e) => setRange(e.target.value)}
            className="appearance-none bg-black/60 border border-white/15 rounded px-2 py-1 pr-6 text-xs text-white outline-none focus:border-emerald-400/60"
            data-testid="nutrient-chart-range"
          >
            <option value="today">Today</option>
            <option value="week">7 days</option>
            <option value="month">30 days</option>
          </select>
          <ChevronDown size={12} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-white/50 pointer-events-none" />
        </div>
      </div>

      {(scoped.length === 0 && quickEntries.length === 0) ? (
        <div className="text-xs text-white/50 italic px-2 py-6 text-center border border-dashed border-white/15 rounded">
          Log some meals in Food Log and this chart fills in.
        </div>
      ) : (
        <>
          {/* Donut + legend */}
          <div className={`flex ${compact ? "flex-row" : "flex-col sm:flex-row"} items-center gap-4`}>
            <div className="relative" style={{ width: 160, height: 160 }}>
              <svg width="160" height="160" viewBox="0 0 160 160" data-testid="nutrient-donut">
                {(() => {
                  let angle = 0;
                  return macroSlices.map((s) => {
                    const start = angle;
                    const end = angle + s.pct * 360;
                    angle = end;
                    return (
                      <DonutSlice
                        key={s.key}
                        startAngle={start}
                        endAngle={end}
                        radius={70}
                        innerRadius={44}
                        color={s.color}
                        cx={80}
                        cy={80}
                      />
                    );
                  });
                })()}
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <div className="text-[9px] uppercase tracking-widest text-white/50">{range === "today" ? "today" : "avg / day"}</div>
                <div className="text-2xl font-black text-white leading-none">{n(range === "today" ? totals.cal : avgCal)}</div>
                <div className="text-[10px] text-white/50 flex items-center gap-1"><Flame size={10} className="text-amber-400" /> kcal</div>
              </div>
            </div>

            <div className="flex-1 min-w-0 space-y-2 w-full">
              {macroSlices.map((s) => (
                <div key={s.key} className="flex items-center gap-2" data-testid={`nutrient-macro-${s.key}`}>
                  <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: s.color }} />
                  <div className="text-xs text-white/85 flex-1">{s.label}</div>
                  <div className="text-xs font-bold text-white tabular-nums">
                    {n(range === "today" ? s.grams : s.grams / days)} g
                  </div>
                  <div className="text-[10px] text-white/50 tabular-nums w-10 text-right">
                    {Math.round(s.pct * 100)}%
                  </div>
                </div>
              ))}
              {range !== "today" && (
                <div className="text-[10px] text-white/40 italic pt-1">
                  Averages across last {days} days ({scoped.length} entries).
                </div>
              )}
              {range === "today" && quickCalToday > 0 && (
                <div className="text-[10px] text-emerald-300/80 italic pt-1" data-testid="nutrient-quick-included">
                  Includes {quickEntries.length} Quick Log extra{quickEntries.length !== 1 ? "s" : ""} · {n(quickCalToday)} kcal
                  {quickWithMacrosCount > 0 && (
                    <span className="text-emerald-300/60"> · {quickWithMacrosCount} with full macros ✅</span>
                  )}
                </div>
              )}
              {/* 🧪✨ Bulk Retro Macro Backfill — scans ALL dates for calorie-only
                  entries and fills them with AI estimates in one tap. Only
                  renders when we know at least ONE of today's entries is
                  missing macros (a proxy for "there might be more historical
                  ones missing too" — the backend confirms the true count). */}
              {(quickEntries.length - quickWithMacrosCount) > 0 && (
                <button
                  onClick={runBulkBackfill}
                  disabled={bulkBusy}
                  data-testid="nutrient-bulk-backfill"
                  className="mt-1 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-emerald-300 hover:text-emerald-200 disabled:opacity-40 transition"
                  title="AI-estimate P/C/F/fiber/sugar/sodium for every calorie-only entry across all dates"
                >
                  {bulkBusy
                    ? <Loader2 size={11} className="animate-spin" />
                    : <Sparkles size={11} />}
                  {bulkBusy ? "Estimating…" : "AI-Fill All Missing Macros"}
                </button>
              )}
            </div>
          </div>

          {/* 🧾 Bulk-backfill receipt modal — shows what the AI filled in */}
          {bulkReceipt && (
            <div
              className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
              onClick={() => setBulkReceipt(null)}
              data-testid="nutrient-bulk-receipt"
            >
              <div
                className="bg-[#0F0F0F] border-2 border-emerald-500/50 max-w-md w-full p-5 max-h-[85vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-300 font-bold flex items-center gap-1">
                      <Sparkles size={11} /> Retro Macro Backfill · Receipt
                    </div>
                    <h3 className="font-display text-lg text-white mt-1 leading-tight">
                      {bulkReceipt.updated} entr{bulkReceipt.updated === 1 ? "y" : "ies"} filled
                      {bulkReceipt.scanned > bulkReceipt.updated && (
                        <span className="text-neutral-500"> · {bulkReceipt.scanned - bulkReceipt.updated} skipped</span>
                      )}
                    </h3>
                  </div>
                  <button
                    onClick={() => setBulkReceipt(null)}
                    className="text-neutral-500 hover:text-white text-lg leading-none"
                    data-testid="nutrient-bulk-receipt-close"
                  >
                    ×
                  </button>
                </div>
                {(bulkReceipt.samples || []).length > 0 ? (
                  <>
                    <div className="text-[9px] uppercase tracking-widest text-neutral-500 mb-1.5">
                      Sample estimates:
                    </div>
                    <ul className="space-y-2 mb-3">
                      {bulkReceipt.samples.map((s, i) => (
                        <li key={i} className="bg-black/40 border border-zinc-800 p-2">
                          <div className="text-sm text-white truncate">{s.name}</div>
                          <div className="text-[10px] text-neutral-400 mt-0.5">
                            <span className="text-emerald-300 font-bold">
                              P{s.estimate?.protein_g}g · C{s.estimate?.carbs_g}g · F{s.estimate?.fat_g}g
                            </span>
                            {" · "}
                            fib{s.estimate?.fiber_g}g · sug{s.estimate?.sugar_g}g · na{s.estimate?.sodium_mg}mg
                            {s.estimate?.confidence && (
                              <span className="text-neutral-500 uppercase tracking-widest ml-1">
                                · {s.estimate.confidence} conf
                              </span>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                    {bulkReceipt.updated > bulkReceipt.samples.length && (
                      <p className="text-[10px] italic text-neutral-500 mb-3">
                        + {bulkReceipt.updated - bulkReceipt.samples.length} more entries — check your logs to review.
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-xs text-neutral-400 italic mb-3">
                    {bulkReceipt.scanned === 0
                      ? "Nothing needed filling — every entry already has full macros."
                      : "AI couldn't estimate any entries. Try again in a moment."}
                  </p>
                )}
                <button
                  onClick={() => setBulkReceipt(null)}
                  data-testid="nutrient-bulk-receipt-done"
                  className="w-full py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs uppercase tracking-widest font-bold transition"
                >
                  Done
                </button>
              </div>
            </div>
          )}

          {/* Daily calorie bars */}
          {days > 1 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/60 font-bold mb-1.5">
                Daily calories
              </div>
              <div className="flex items-end gap-1 h-24" data-testid="nutrient-daily-bars">
                {dailyBuckets.map((b) => {
                  const h = b.cal > 0 ? Math.max(4, (b.cal / maxDaily) * 96) : 2;
                  const label = b.date.slice(5); // MM-DD
                  return (
                    <div key={b.date} className="flex-1 flex flex-col items-center gap-1 min-w-0" title={`${b.date}: ${n(b.cal)} kcal`}>
                      <div
                        className={`w-full rounded-t transition ${b.cal > 0 ? "bg-emerald-500/70" : "bg-white/10"}`}
                        style={{ height: h }}
                      />
                      <div className="text-[8px] text-white/40 tabular-nums truncate">{label}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Micro rows */}
          <div className="space-y-1.5" data-testid="nutrient-micros">
            <div className="text-[10px] uppercase tracking-widest text-white/60 font-bold">
              Also tracked {range !== "today" && "(avg / day)"}
            </div>
            {microRefs.map((m) => {
              const total = totals[m.key];
              const perDay = range === "today" ? total : total / days;
              const pct = Math.min(1.5, perDay / m.daily); // cap at 150%
              const barPct = Math.min(1, pct);
              const high = m.warnHigh && pct > 1;
              return (
                <div key={m.key} className="space-y-0.5" data-testid={`nutrient-micro-${m.key}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs text-white/85">{m.label}</div>
                    <div className="text-[10px] text-white/60 tabular-nums">
                      <span className={`font-bold ${high ? "text-rose-300" : "text-white"}`}>{n(perDay)} {m.unit}</span>
                      <span className="mx-1 text-white/30">/</span>
                      <span>{m.daily.toLocaleString()} {m.unit}</span>
                    </div>
                  </div>
                  <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${barPct * 100}%`,
                        background: high ? "#fb7185" : m.color,
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export default NutrientChart;
